import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { identify, track } from '../lib/analytics';
import { AuthError, devSignIn, loadAuthStatus, setSignInOpen, signUp, type AuthStatus } from '../lib/auth';
import { keyraRedirectUri, loadKeyraSdk, storeLoginNext } from '../lib/keyra';
import { COUNTRIES, defaultCountry, isValidEmail, plausibleNumber, type Country } from '../lib/phone';
import { strings } from '../lib/strings';
import { ActivityIndicator } from './ActivityIndicator';
import { AlertIcon, EyeIcon, EyeOffIcon } from './Icons';
import { RollLabel } from './RollLabel';
import { Select } from './Select';

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.iso, label: c.name, detail: `+${c.dial}` }));

const TERMS_URL = String(import.meta.env.VITE_CIRIGHT_TERMS_URL ?? '').trim() || null;
const PRIVACY_URL = String(import.meta.env.VITE_CIRIGHT_PRIVACY_URL ?? '').trim() || null;

export function InlineError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="rise-in mt-2 flex items-start gap-2 px-1 text-sm font-medium text-danger">
      <AlertIcon className="mt-px h-4 w-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function PrimaryButton({ busy, busyLabel, disabled, children }: { busy: boolean; busyLabel: string; disabled?: boolean; children: string }) {
  return (
    <button
      type="submit"
      disabled={disabled || busy}
      className="cta pressable mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
    >
      {busy ? (
        <>
          <ActivityIndicator size="sm" />
          {busyLabel}
        </>
      ) : (
        <RollLabel>{children}</RollLabel>
      )}
    </button>
  );
}

const rowClass =
  'relative flex min-h-[50px] items-center gap-3 px-4 [&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:left-4 [&:not(:first-child)]:before:right-0 [&:not(:first-child)]:before:top-0 [&:not(:first-child)]:before:h-px [&:not(:first-child)]:before:bg-line';
const inputClass = 'signin-input min-w-0 flex-1 bg-transparent py-3 text-lg text-ink placeholder:text-subtle';
const labelClass = 'w-[92px] shrink-0 text-base text-ink';

/* ---------- Keyra ---------- */

/**
 * Keyra's hosted button (redirect mode), as in the other Ciright apps. Keyra
 * sends the visitor to /callback, which hands the token to the server.
 */
export function KeyraSignIn({ returnTo }: { returnTo: string | null }) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [devBusy, setDevBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | undefined;
    (async () => {
      try {
        const s = await loadAuthStatus();
        if (cancelled) return;
        setStatus(s);
        if (!s.keyra.configured || !s.keyra.clientId) {
          setState('unavailable');
          return;
        }
        const sdk = await loadKeyraSdk();
        if (cancelled || !mountRef.current) return;
        const handle = sdk.renderButton(mountRef.current, {
          clientId: s.keyra.clientId,
          authOrigin: s.keyra.authOrigin,
          redirectUri: keyraRedirectUri(s.keyra.redirectUri),
          scope: 'verify',
          mode: 'redirect',
          appearance: { theme: 'black', text: 'login_with' },
          // Redirect mode finishes on /callback; these only fire if Keyra falls back to a popup.
          onSuccess: () => window.location.assign('/callback'),
          onError: () => setError(strings.callback.failed),
        });
        detach = handle?.detach;
        setState('ready');
      } catch {
        if (!cancelled) setState('unavailable');
      }
    })();
    return () => {
      cancelled = true;
      detach?.();
    };
  }, []);

  // Remember where the visitor was, for when Keyra sends them back.
  useEffect(() => storeLoginNext(returnTo), [returnTo]);

  const continueAsDev = async () => {
    setDevBusy(true);
    setError(null);
    try {
      const session = await devSignIn();
      identify(session.profile.uid);
      track('login_succeeded', { sign_in_method: 'dev' });
      setSignInOpen(false);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : strings.account_.genericError);
    } finally {
      setDevBusy(false);
    }
  };

  return (
    <div className="mt-6">
      <div
        className="flex min-h-12 items-center justify-center [&_button]:!w-full"
        onPointerDown={() => track('login_started', { sign_in_method: 'keyra' })}
      >
        {state === 'loading' && (
          <span className="flex items-center gap-2 text-sm text-muted">
            <ActivityIndicator size="sm" /> {strings.account_.loadingKeyra}
          </span>
        )}
        {state === 'unavailable' && <p className="text-center text-sm text-muted">{strings.account_.keyraUnavailable}</p>}
        <div ref={mountRef} className={state === 'ready' ? 'w-full' : 'hidden'} aria-label={strings.account_.continueWithKeyra} />
      </div>
      {status?.devBypass && (
        <button
          type="button"
          onClick={continueAsDev}
          disabled={devBusy}
          className="pressable mt-3 flex h-[44px] w-full items-center justify-center gap-2 rounded-md bg-fill text-sm font-semibold text-ink hover:bg-fill-strong disabled:opacity-50"
          title={strings.account_.devOnly}
        >
          {devBusy && <ActivityIndicator size="sm" />}
          {strings.account_.devSignIn}
        </button>
      )}
      {error && <InlineError>{error}</InlineError>}
    </div>
  );
}

/* ---------- signup ---------- */

interface Draft {
  name: string;
  country: Country;
  number: string;
  email: string;
  password: string;
  terms: boolean;
}

type Field = 'name' | 'phone' | 'email' | 'password' | 'terms';

function validate(d: Draft): Partial<Record<Field, string>> {
  const errors: Partial<Record<Field, string>> = {};
  if (d.name.trim().length < 2) errors.name = strings.account_.nameRequired;
  if (!plausibleNumber(d.country, d.number)) errors.phone = strings.account_.mobileInvalid;
  if (!isValidEmail(d.email)) errors.email = strings.account_.emailInvalid;
  if (d.password.length < 8) errors.password = strings.account_.passwordShort;
  if (!d.terms) errors.terms = strings.account_.termsRequired;
  return errors;
}

export function SignupFlow({ returnTo, onCreated }: { returnTo: string | null; onCreated: (message: string) => void }) {
  const navigate = useNavigate();
  const ids = { name: useId(), email: useId(), password: useId(), country: useId(), error: useId() };
  const [draft, setDraft] = useState<Draft>(() => ({ name: '', country: defaultCountry(), number: '', email: '', password: '', terms: false }));
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [serverFields, setServerFields] = useState<Record<string, string>>({});
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const local = validate(draft);
  const shown = (f: Field) => (submitted || touched[f] ? local[f] : undefined) ?? serverFields[f];
  const firstError = (['name', 'phone', 'email', 'password', 'terms'] as const).map(shown).find(Boolean);

  const update = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setServerFields({});
    setError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(local).length) return;
    setBusy(true);
    setError(null);
    track('registration_submitted', {});
    try {
      const outcome = await signUp({
        name: draft.name,
        email: draft.email,
        dialCode: draft.country.dial,
        number: draft.number,
        password: draft.password,
        termsAccepted: draft.terms,
      });
      if (outcome.status === 'complete') {
        identify(outcome.session.profile.uid);
        track('registration_completed', { signed_in: true });
        setSignInOpen(false);
        if (returnTo) navigate(returnTo, { replace: true });
      } else {
        track('registration_completed', { signed_in: false });
        onCreated(outcome.message);
      }
    } catch (err) {
      track('registration_failed', { reason: err instanceof AuthError ? err.code : 'unexpected' });
      if (err instanceof AuthError && Object.keys(err.fields).length) setServerFields(err.fields);
      setError(err instanceof AuthError ? err.message : strings.account_.genericError);
    } finally {
      setBusy(false);
      setDraft((d) => ({ ...d, password: '' }));
    }
  };

  const link = (href: string | null, label: string) =>
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline-offset-2 hover:underline">
        {label}
      </a>
    ) : (
      label
    );

  return (
    <form className="mt-6" onSubmit={submit} noValidate>
      <fieldset disabled={busy} className="min-w-0">
        <legend className="sr-only">{strings.account_.registerTitle}</legend>
        <div className="signin-group overflow-hidden rounded-md bg-canvas ring-1 ring-black/5">
          <div className={rowClass}>
            <label htmlFor={ids.name} className={labelClass}>
              {strings.account_.fullName}
            </label>
            <input
              id={ids.name}
              name="name"
              autoComplete="name"
              enterKeyHint="next"
              value={draft.name}
              aria-invalid={Boolean(shown('name'))}
              onChange={(e) => update('name', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              className={inputClass}
              placeholder={strings.account_.required}
            />
          </div>
          <div className={rowClass}>
            <label htmlFor={ids.email} className={labelClass}>
              {strings.account_.email}
            </label>
            <input
              id={ids.email}
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              enterKeyHint="next"
              value={draft.email}
              aria-invalid={Boolean(shown('email'))}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              className={inputClass}
              placeholder="name@example.com"
            />
          </div>
          <div className={rowClass}>
            <label htmlFor={ids.country} className={labelClass}>
              {strings.account_.mobile}
            </label>
            <Select
              id={ids.country}
              aria-label={strings.account_.countryCode}
              value={draft.country.iso}
              options={COUNTRY_OPTIONS}
              onChange={(iso) => update('country', COUNTRIES.find((c) => c.iso === iso) ?? draft.country)}
              display={<span className="tabular-nums">{`${draft.country.iso} +${draft.country.dial}`}</span>}
              className="h-[32px] shrink-0 rounded-full pl-3 pr-2 text-base"
            />
            <input
              name="mobile"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              aria-label={strings.account_.mobileNumber}
              enterKeyHint="next"
              value={draft.number}
              aria-invalid={Boolean(shown('phone'))}
              onChange={(e) => update('number', e.target.value.replace(/[^\d\s()-]/g, ''))}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              className={inputClass}
              placeholder={strings.account_.mobilePlaceholder}
            />
          </div>
          <div className={`${rowClass} pr-2`}>
            <label htmlFor={ids.password} className={labelClass}>
              {strings.password}
            </label>
            <input
              id={ids.password}
              name="new-password"
              type={reveal ? 'text' : 'password'}
              autoComplete="new-password"
              enterKeyHint="go"
              value={draft.password}
              aria-invalid={Boolean(shown('password'))}
              onChange={(e) => update('password', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              className={inputClass}
              placeholder={strings.account_.newPassword}
            />
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? strings.hidePassword : strings.showPassword}
              aria-pressed={reveal}
              className="pressable flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full text-subtle hover:bg-fill hover:text-ink"
            >
              {reveal ? <EyeOffIcon className="h-[18px] w-[18px]" /> : <EyeIcon className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </div>

        <div aria-live="polite" className="min-h-[24px] px-1 text-sm">
          {firstError && <InlineError id={ids.error}>{firstError}</InlineError>}
        </div>

        <label className="mt-2 flex cursor-pointer items-start gap-3 px-1 text-sm text-ink">
          <input
            type="checkbox"
            checked={draft.terms}
            onChange={(e) => update('terms', e.target.checked)}
            className="ios-check"
            aria-invalid={Boolean(shown('terms'))}
          />
          <span>
            {strings.account_.termsPrefix} {link(TERMS_URL, strings.account_.terms)} {strings.account_.and}{' '}
            {link(PRIVACY_URL, strings.account_.privacy)}.
          </span>
        </label>
      </fieldset>

      <div aria-live="assertive">{error && <InlineError>{error}</InlineError>}</div>

      <PrimaryButton busy={busy} busyLabel={strings.account_.creating}>
        {strings.account_.createAccount}
      </PrimaryButton>
      <p className="mt-4 text-center text-sm text-muted">
        {strings.account_.haveAccount}{' '}
        <button type="button" onClick={() => setSignInOpen(true, 'sign-in')} className="pressable rounded-full px-1 font-semibold text-primary hover:bg-fill">
          {strings.signIn}
        </button>
      </p>
    </form>
  );
}
